/**
 * Vercel deep dive — the usage truth behind the Ramp "Vercel" vendor line, read live off
 * `cloud_costs.vercel_daily` (collector-written, one row per day × project × meter).
 *
 * Three things differ from the GCP tab beyond the vocabulary:
 *
 * 1. Projects ride in `month.accounts` (the plain AWS shape) with no billing groups and no
 *    featured pins — Vercel has one team and one bill.
 * 2. The drill-down is a *usage* drawer, not a resource drawer: the billing API is per
 *    project, so the interesting question is what each meter consumed, not on which
 *    resource it landed.
 * 3. The table always lags a day — Vercel's billing day is Pacific-anchored and the
 *    collector lands through yesterday — so the forecast's `coveredThrough` is surfaced as
 *    data freshness rather than left to imply that the number is current.
 */

import { todayUTC } from '@/../../shared/dates';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange, Crown, Triangle, TrendingUp } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  useFinopsHistory,
  useFinopsVercelForecast,
  useFinopsVercelMonth,
  useFinopsVercelUsage,
  useFinopsVercelWeekly,
} from '@/hooks/useFinops';
import { AccountOrProjectTable, type EntityRow } from './AccountOrProjectTable';
import { ForecastCard } from './ForecastCard';
import { HistoryStrip, type HistoryGranularity } from './HistoryStrip';
import { ServiceTable } from './ServiceTable';
import { UsageDrawer } from './UsageDrawer';
import { WeeklyDigestPanel } from './WeeklyDigestPanel';
import {
  EmptyState, LoaderBlock, StatCard, addDaysISO, deltaOf, fmtDay, fmtDelta, fmtMonth, fmtUSD,
  fmtWeekRange,
} from './shared';

const ALL_PROJECTS = '__all__';
/** 3 months ≈ the 90-day default; each "Load more" pages ~60 days further back. */
const HISTORY_START_MONTHS = 3;
const HISTORY_STEP_MONTHS = 2;
const HISTORY_MAX_MONTHS = 36;

/**
 * Charges Vercel does not attribute to a project arrive with empty tags and are bucketed
 * under this id by the helper (`VERCEL_TEAM_ENTITY`). It is the largest row on the bill and
 * it is NOT flat overhead — seats and the plan fee sit alongside genuinely usage-based
 * meters (Blob Data Transfer, Speed Insights Plus, Blob Storage Size, Fast Origin Transfer),
 * so the label says "not attributed to a project", never "seats & plan".
 */
const TEAM_ENTITY = '(team)';

/** Below this, the credit line would read "$0 absorbed" — say nothing was absorbed instead. */
const CREDIT_EPSILON = 0.5;
/** The feed lags one day by design; beyond this it is behind, and the card says so. */
const STALE_AFTER_DAYS = 2;

const todayISO = todayUTC; // los períodos de Vercel son días UTC

export function FinopsVercelTab() {
  const { t } = useTranslation();

  const [project, setProject] = useState<string>(ALL_PROJECTS);
  const [drawerService, setDrawerService] = useState<string | undefined>();
  const [historyMonths, setHistoryMonths] = useState(HISTORY_START_MONTHS);
  const [granularity, setGranularity] = useState<HistoryGranularity>('month');

  const { data: month, isLoading: monthLoading } = useFinopsVercelMonth();
  const { data: week, isLoading: weekLoading } = useFinopsVercelWeekly();
  // Per-service WoW deltas need the previous week, which only exists once the
  // current week's period is known — hence the deferred second query.
  const priorWeekStart = week ? addDaysISO(week.period, -7) : undefined;
  const { data: priorWeek } = useFinopsVercelWeekly(priorWeekStart, !!priorWeekStart);
  const { data: forecast } = useFinopsVercelForecast();
  const { data: history, isFetching: historyFetching } = useFinopsHistory('vercel', historyMonths);

  const drawerOpen = !!drawerService;
  // The filter travels as the project *id* — which is what the helper's WHERE compares
  // against, so the '(team)' bucket is selectable like any other project.
  const { data: usage = [], isLoading: usageLoading } = useFinopsVercelUsage(
    week?.period ?? '',
    project === ALL_PROJECTS ? undefined : project,
    drawerService,
    drawerOpen && !!week?.period,
  );

  // Rows keep the backend's cost order. No groups, no pins: one team, one bill.
  const projectRows: EntityRow[] = useMemo(
    () => (month?.accounts ?? []).map((a) => ({
      id: a.id,
      name: a.id === TEAM_ENTITY ? t('finops.vercel.team_row') : a.name,
      cost: a.cost,
    })),
    [month, t],
  );

  // Project filter is a client-side fold of the matrix that already travelled.
  const services = useMemo(() => {
    if (!month) return [];
    if (project === ALL_PROJECTS) return month.services;
    const byService = new Map<string, number>();
    for (const row of month.matrix) {
      if (row.accountId !== project) continue;
      byService.set(row.service, (byService.get(row.service) ?? 0) + row.cost);
    }
    return Array.from(byService.entries())
      .map(([service, cost]) => ({ service, cost }))
      .sort((a, b) => b.cost - a.cost);
  }, [month, project]);

  // Usage rows carry the project *name*, so the team bucket needs the same relabel.
  const usageRows = useMemo(
    () => usage.map((r) => (
      r.project === TEAM_ENTITY ? { ...r, project: t('finops.vercel.team_row') } : r
    )),
    [usage, t],
  );

  /**
   * The card's note carries three facts, each independently optional: the run-rate itself,
   * how much of the month's usage the plan's included credit absorbed (a roughly constant
   * ~$30 slice, so it is stated in dollars and never as a percentage), and how fresh the
   * data behind both is.
   */
  const forecastNote = useMemo(() => {
    if (!forecast) return undefined;
    const parts = [t('finops.vercel.forecast_note', {
      avg: fmtUSD(forecast.dailyAvg7d),
      elapsed: forecast.daysElapsed,
      total: forecast.daysInMonth,
    })];

    if (forecast.billedMtd != null) {
      const credit = forecast.mtdActual - forecast.billedMtd;
      parts.push(credit >= CREDIT_EPSILON
        ? t('finops.vercel.forecast_credit', {
            credit: fmtUSD(credit), billed: fmtUSD(forecast.billedMtd),
          })
        : t('finops.vercel.forecast_credit_none', { mtd: fmtUSD(forecast.mtdActual) }));
    }

    if (forecast.coveredThrough) {
      const stale = forecast.coveredThrough < addDaysISO(todayISO(), -STALE_AFTER_DAYS);
      parts.push(t(stale ? 'finops.vercel.forecast_stale' : 'finops.vercel.forecast_covered', {
        date: fmtDay(forecast.coveredThrough),
      }));
    }

    return parts.join(' · ');
  }, [forecast, t]);

  if (monthLoading && weekLoading) return <LoaderBlock />;

  const wow = week ? deltaOf(week.total, week.priorTotal) : null;
  const projectName = projectRows.find((p) => p.id === project)?.name;
  const canLoadMore = historyMonths < HISTORY_MAX_MONTHS;

  return (
    <div className="space-y-4">
      {/* 1 — Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={CalendarRange}
          tone="blue"
          label={t('finops.vercel.stat_prev_month')}
          value={fmtUSD(month?.total)}
          sub={month?.period ? fmtMonth(month.period) : undefined}
        />
        <StatCard
          icon={Triangle}
          tone="purple"
          label={t('finops.vercel.stat_week')}
          value={fmtUSD(week?.total)}
          sub={week ? `${fmtWeekRange(week.period)} · ${fmtDelta(wow)} ${t('finops.cloud.digest_wow')}` : undefined}
        />
        <StatCard
          icon={TrendingUp}
          tone={
            forecast && forecast.priorMonthActual > 0 && forecast.projected >= forecast.priorMonthActual
              ? 'rose'
              : 'green'
          }
          label={t('finops.vercel.stat_projection')}
          value={fmtUSD(forecast?.projected)}
          sub={
            forecast
              ? t('finops.vercel.stat_projection_sub', {
                  delta: fmtDelta(deltaOf(forecast.projected, forecast.priorMonthActual)),
                })
              : undefined
          }
        />
        <StatCard
          icon={Crown}
          tone="amber"
          label={t('finops.vercel.stat_top_service')}
          value={month?.services[0]?.service ?? '—'}
          sub={month?.services[0] ? fmtUSD(month.services[0].cost) : undefined}
        />
      </div>

      {/* 2 — Weekly digest (hero) + run-rate projection */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <WeeklyDigestPanel
            week={week}
            priorWeek={priorWeek}
            emptyDescription={t('finops.vercel.week_empty_description')}
          />
        </div>
        {/* No `interval` and no `snapshotDate`: this is a live run-rate, not a
            Cost Explorer forecast, and the card must not imply otherwise. */}
        <ForecastCard
          period={forecast?.period}
          projected={forecast?.projected}
          mtdActual={forecast?.mtdActual}
          priorMonthActual={forecast?.priorMonthActual}
          badge={t('finops.vercel.forecast_badge')}
          note={forecastNote}
          emptyDescription={t('finops.vercel.forecast_empty_description')}
        />
      </div>

      {/* 3 — Previous-month detail */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <h2 className="text-sm font-semibold">
          {t('finops.vercel.detail_title')}
          {month?.period && (
            <span className="text-muted-foreground font-normal"> · {fmtMonth(month.period)}</span>
          )}
        </h2>
        <Select value={project} onValueChange={setProject}>
          <SelectTrigger className="h-8 text-sm w-64">
            <SelectValue placeholder={t('finops.vercel.project_filter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_PROJECTS}>{t('finops.vercel.all_projects')}</SelectItem>
            {projectRows.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!month || month.total === 0 ? (
        <EmptyState
          title={t('finops.cloud.month_empty_title')}
          description={t('finops.vercel.month_empty_description')}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AccountOrProjectTable
            title={t('finops.vercel.projects_title')}
            rows={projectRows}
            total={month.total}
            selectedId={project === ALL_PROJECTS ? undefined : project}
            onSelect={(id) => setProject(id ?? ALL_PROJECTS)}
            entityColLabel={t('finops.vercel.col_project')}
            emptyDescription={t('finops.vercel.month_empty_description')}
          />
          <ServiceTable
            title={t('finops.cloud.services_title')}
            subtitle={project === ALL_PROJECTS ? undefined : projectName ?? project}
            rows={services}
            onOpenResources={week?.period ? setDrawerService : undefined}
          />
        </div>
      )}

      {/* 4 — History */}
      <HistoryStrip
        data={history}
        granularity={granularity}
        onGranularityChange={setGranularity}
        onLoadMore={() => setHistoryMonths((m) => Math.min(HISTORY_MAX_MONTHS, m + HISTORY_STEP_MONTHS))}
        canLoadMore={canLoadMore}
        isLoading={historyFetching}
        emptyDescription={t('finops.vercel.history_empty_description')}
      />

      {/* 5 — Footnote */}
      <p className="text-xs text-muted-foreground">{t('finops.vercel.footnote')}</p>

      <UsageDrawer
        open={drawerOpen}
        onOpenChange={(open) => !open && setDrawerService(undefined)}
        service={drawerService}
        periodLabel={week?.period ? fmtWeekRange(week.period) : undefined}
        rows={usageRows}
        isLoading={usageLoading}
        entityColLabel={t('finops.vercel.col_project')}
        emptyDescription={t('finops.vercel.usage_empty_description')}
        filterLabel={project === ALL_PROJECTS ? undefined : projectName ?? project}
      />
    </div>
  );
}
